// =============================================================================
// VALIDATION SCHEMAS (Zod)
// =============================================================================

import { z } from 'zod';

// =============================================================================
// Common validators
// =============================================================================

export const emailSchema = z.string().email('Email inválido');

export const phoneSchema = z.string()
    .regex(/^\+?[1-9]\d{10,14}$/, 'Telefone inválido')
    .transform(v => v.replace(/\D/g, ''));

export const cpfSchema = z.string()
    .regex(/^\d{11}$/, 'CPF deve ter 11 dígitos')
    .refine(validateCPF, 'CPF inválido');

export const cnpjSchema = z.string()
    .regex(/^\d{14}$/, 'CNPJ deve ter 14 dígitos')
    .refine(validateCNPJ, 'CNPJ inválido');

export const documentSchema = z.union([cpfSchema, cnpjSchema]);

export const passwordSchema = z.string()
    .min(8, 'Senha deve ter no mínimo 8 caracteres')
    .regex(/[a-z]/, 'Senha deve conter letra minúscula')
    .regex(/[A-Z]/, 'Senha deve conter letra maiúscula')
    .regex(/[0-9]/, 'Senha deve conter número');

export const slugSchema = z.string()
    .min(3, 'Slug deve ter no mínimo 3 caracteres')
    .max(50, 'Slug deve ter no máximo 50 caracteres')
    .regex(/^[a-z0-9-]+$/, 'Slug deve conter apenas letras minúsculas, números e hífens');

// =============================================================================
// User schemas
// =============================================================================

export const signUpSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    full_name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
    phone: phoneSchema.optional(),
    role: z.enum(['personal', 'student']),
    affiliate_code: z.string().optional()
});

export const signInSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Senha é obrigatória')
});

export const updateProfileSchema = z.object({
    full_name: z.string().min(2).optional(),
    phone: phoneSchema.optional(),
    birth_date: z.string().optional(),
    gender: z.enum(['male', 'female', 'other', 'prefer_not_to_say']).optional(),
    height_cm: z.number().min(100).max(250).optional(),
    goals: z.array(z.string()).optional(),
    injuries: z.array(z.string()).optional(),
    experience_level: z.enum(['beginner', 'intermediate', 'advanced']).optional()
});

// =============================================================================
// Tenant schemas
// =============================================================================

export const createTenantSchema = z.object({
    name: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
    slug: slugSchema,
    logo_url: z.string().url().optional()
});

export const updateTenantSchema = createTenantSchema.partial();

// =============================================================================
// Workout schemas
// =============================================================================

export const createExerciseSchema = z.object({
    name: z.string().min(2),
    description: z.string().optional(),
    instructions: z.string().optional(),
    muscle_groups: z.array(z.string()).min(1, 'Selecione ao menos um grupo muscular'),
    equipment: z.array(z.string()).optional(),
    difficulty: z.enum(['beginner', 'intermediate', 'advanced']),
    video_url: z.string().url().optional(),
    thumbnail_url: z.string().url().optional()
});

export const createWorkoutSchema = z.object({
    name: z.string().min(2),
    description: z.string().optional(),
    estimated_duration_minutes: z.number().min(1).max(300).optional(),
    difficulty: z.enum(['beginner', 'intermediate', 'advanced']).optional(),
    category: z.string().optional(),
    tags: z.array(z.string()).optional()
});

export const createBlockSchema = z.object({
    name: z.string().min(1),
    type: z.enum(['warmup', 'main', 'cooldown', 'superset', 'circuit', 'emom']),
    rest_between_exercises_seconds: z.number().min(0).optional(),
    notes: z.string().optional()
});

export const addBlockExerciseSchema = z.object({
    exercise_id: z.string().uuid(),
    sets: z.number().min(1).max(20).optional(),
    reps: z.string().optional(), // Can be "12" or "8-12" or "AMRAP"
    weight_kg: z.number().min(0).optional(),
    duration_seconds: z.number().min(0).optional(),
    rest_seconds: z.number().min(0).optional(),
    tempo: z.string().optional(),
    rpe: z.number().min(1).max(10).optional(),
    notes: z.string().optional()
});

export const logWorkoutSetSchema = z.object({
    block_exercise_id: z.string().uuid(),
    set_number: z.number().min(1),
    reps_completed: z.number().min(0).optional(),
    weight_kg: z.number().min(0).optional(),
    duration_seconds: z.number().min(0).optional(),
    rpe: z.number().min(1).max(10).optional(),
    notes: z.string().optional()
});

// =============================================================================
// Progress schemas
// =============================================================================

export const logProgressSchema = z.object({
    entry_date: z.string(),
    type: z.enum(['weight', 'measurement', 'photo', 'body_composition']),
    weight_kg: z.number().min(20).max(500).optional(),
    body_fat_percent: z.number().min(1).max(80).optional(),
    muscle_mass_kg: z.number().min(10).max(200).optional(),
    measurements: z.object({
        chest_cm: z.number().optional(),
        waist_cm: z.number().optional(),
        hips_cm: z.number().optional(),
        thigh_left_cm: z.number().optional(),
        thigh_right_cm: z.number().optional(),
        arm_left_cm: z.number().optional(),
        arm_right_cm: z.number().optional(),
        calf_left_cm: z.number().optional(),
        calf_right_cm: z.number().optional()
    }).optional(),
    notes: z.string().optional()
});

export const checkInSchema = z.object({
    energy_level: z.number().min(1).max(5).optional(),
    sleep_quality: z.number().min(1).max(5).optional(),
    stress_level: z.number().min(1).max(5).optional(),
    nutrition_adherence: z.number().min(1).max(5).optional(),
    workout_adherence: z.number().min(1).max(5).optional(),
    notes: z.string().optional(),
    questions_answers: z.record(z.string()).optional()
});

// =============================================================================
// Affiliate schemas
// =============================================================================

export const createInfluencerSchema = z.object({
    name: z.string().min(2),
    email: emailSchema,
    phone: phoneSchema.optional(),
    document: documentSchema.optional(),
    pix_key: z.string().optional(),
    bank_account: z.object({
        bank: z.string(),
        agency: z.string(),
        account: z.string(),
        type: z.enum(['checking', 'savings'])
    }).optional(),
    payout_method: z.enum(['pix', 'bank_transfer']),
    notes: z.string().optional()
});

export const createAffiliateCodeSchema = z.object({
    influencer_id: z.string().uuid(),
    code: z.string()
        .min(3, 'Código deve ter no mínimo 3 caracteres')
        .max(20, 'Código deve ter no máximo 20 caracteres')
        .regex(/^[A-Z0-9]+$/, 'Código deve conter apenas letras maiúsculas e números')
        .transform(v => v.toUpperCase()),
    link_slug: slugSchema,
    max_uses: z.number().min(1).optional(),
    valid_until: z.string().optional()
});

export const applyAffiliateCodeSchema = z.object({
    code: z.string().min(1, 'Código é obrigatório')
});

// =============================================================================
// WhatsApp schemas
// =============================================================================

export const sendWhatsAppSchema = z.object({
    student_id: z.string().uuid(),
    template_id: z.string().uuid(),
    variables: z.record(z.string()).optional()
});

// =============================================================================
// Content schemas
// =============================================================================

export const curateContentSchema = z.object({
    platform: z.enum(['youtube', 'spotify']),
    content_type: z.enum(['video', 'channel', 'playlist', 'podcast', 'episode']),
    external_id: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    thumbnail_url: z.string().url().optional(),
    creator_name: z.string().optional(),
    category: z.string().optional(),
    notes: z.string().optional()
});

// =============================================================================
// Sensor schemas
// =============================================================================

export const grantConsentSchema = z.object({
    consent_type: z.enum(['wearable', 'bioimpedance', 'location'])
});

export const logSensorDataSchema = z.object({
    data_type: z.enum(['steps', 'heart_rate', 'sleep', 'calories', 'body_composition', 'location']),
    source: z.string().min(1),
    recorded_at: z.string(),
    value: z.number(),
    unit: z.string(),
    metadata: z.record(z.unknown()).optional()
});

// =============================================================================
// Validation helpers
// =============================================================================

function validateCPF(cpf: string): boolean {
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) {
        sum += parseInt(cpf[i]) * (10 - i);
    }
    let digit = (sum * 10) % 11;
    if (digit === 10) digit = 0;
    if (digit !== parseInt(cpf[9])) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += parseInt(cpf[i]) * (11 - i);
    }
    digit = (sum * 10) % 11;
    if (digit === 10) digit = 0;
    if (digit !== parseInt(cpf[10])) return false;

    return true;
}

function validateCNPJ(cnpj: string): boolean {
    if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;

    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    let sum = 0;
    for (let i = 0; i < 12; i++) {
        sum += parseInt(cnpj[i]) * weights1[i];
    }
    let digit = sum % 11;
    digit = digit < 2 ? 0 : 11 - digit;
    if (digit !== parseInt(cnpj[12])) return false;

    sum = 0;
    for (let i = 0; i < 13; i++) {
        sum += parseInt(cnpj[i]) * weights2[i];
    }
    digit = sum % 11;
    digit = digit < 2 ? 0 : 11 - digit;
    if (digit !== parseInt(cnpj[13])) return false;

    return true;
}

// Type exports
export type SignUpData = z.infer<typeof signUpSchema>;
export type SignInData = z.infer<typeof signInSchema>;
export type UpdateProfileData = z.infer<typeof updateProfileSchema>;
export type CreateTenantData = z.infer<typeof createTenantSchema>;
export type CreateExerciseData = z.infer<typeof createExerciseSchema>;
export type CreateWorkoutData = z.infer<typeof createWorkoutSchema>;
export type CreateBlockData = z.infer<typeof createBlockSchema>;
export type AddBlockExerciseData = z.infer<typeof addBlockExerciseSchema>;
export type LogWorkoutSetData = z.infer<typeof logWorkoutSetSchema>;
export type LogProgressData = z.infer<typeof logProgressSchema>;
export type CheckInData = z.infer<typeof checkInSchema>;
export type CreateInfluencerData = z.infer<typeof createInfluencerSchema>;
export type CreateAffiliateCodeData = z.infer<typeof createAffiliateCodeSchema>;
export type ApplyAffiliateCodeData = z.infer<typeof applyAffiliateCodeSchema>;
export type SendWhatsAppData = z.infer<typeof sendWhatsAppSchema>;
export type CurateContentData = z.infer<typeof curateContentSchema>;
export type GrantConsentData = z.infer<typeof grantConsentSchema>;
export type LogSensorData = z.infer<typeof logSensorDataSchema>;
